import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import SmsIcon from '@/assets/icons/annotation.svg';
import CursorClickIcon from '@/assets/icons/cursor-click.svg';
import MailIcon from '@/assets/icons/mail.svg';
import MailOpenIcon from '@/assets/icons/mail-open.svg';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

interface PerformanceStatsProps {
  delivered?: number | null;
  opened?: number | null;
  clicked?: number | null;
  campaignType: 'email' | 'sms';
  displayCampaignType: string;
  isFetching: boolean;
}

export function PerformanceStats({
  delivered,
  opened,
  clicked,
  campaignType,
  displayCampaignType,
  isFetching,
}: PerformanceStatsProps) {
  const intl = useIntl();

  const performanceStats = useMemo(() => {
    return [
      {
        label: intl.formatMessage(
          {
            defaultMessage: '{displayCampaignType} deliveries',
            description: 'Deliveries label',
          },
          { displayCampaignType },
        ),
        value: delivered,
        icon: campaignType === 'email' ? MailIcon : SmsIcon,
        supportedTypes: ['email', 'sms'],
      },
      {
        label: intl.formatMessage(
          {
            defaultMessage: '{displayCampaignType} open',
            description: 'Opens label',
          },
          { displayCampaignType },
        ),
        value: opened,
        icon: MailOpenIcon,
        supportedTypes: ['email'],
      },
      {
        label: intl.formatMessage(
          {
            defaultMessage: '{displayCampaignType} clicks',
            description: 'Clicks label',
          },
          { displayCampaignType },
        ),
        value: clicked,
        icon: CursorClickIcon,
        supportedTypes: ['email'],
      },
    ];
  }, [intl, displayCampaignType, delivered, campaignType, opened, clicked]);

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="caption">
        <FormattedMessage
          defaultMessage="Performance stats"
          description="Performance stats caption"
        />
      </Typography>

      <div
        className={`grid gap-4 ${
          campaignType === 'sms' ? 'grid-cols-1' : 'grid-cols-3'
        }`}
      >
        {performanceStats
          .filter(stat => stat.supportedTypes.includes(campaignType))
          .map(stat => (
            <div
              key={stat.label}
              className="border-fx-neutral-300 flex flex-col rounded-lg border p-3 shadow-sm"
            >
              <img src={stat.icon} alt={stat.label} className="h-3 w-3" />
              <Typography variant="subtitle1">{stat.label}</Typography>
              {isFetching ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <Typography variant="body2">{stat.value ?? '-'}</Typography>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
